import Badge from "@/common/components/Badge";
import Box from "@/common/components/Box";
import { BoxesIcon } from "lucide-react";
import { useEffect, useId, useMemo } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import ProductDistributionFormItem from "./ProductDistributionFormItem";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { CreateGovernmentAllocationDistributionValidationSchema, CreateGovernmentAllocationDistributionQuantityValidationSchema } from "@/features/privateCompanies/schemas/createGovernmentAllocationDistribution.schema";
import { formatCurrency, formatDecimalUnit } from "@/common/utils/formatters";
import Select from "@/common/components/Select";
import { CompanyGovernmentAllocationDistribution, CompanyGovernmentAllocationProductCategoriesDistribution, CompanyGovernmentAllocationProductDistribution, ControlledByType, GovernmentAllocationAllowedCategoriesAndProducts, GovernmentAllocationAllowedCategoryAndProductData } from "@/features/privateCompanies/types/companyGovernmentAllocation.type";
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { GovernmentAllocationsAllowedCategoriesAndProductsTransformer } from '@/features/privateCompanies/services/governmentAllocationAllowedCategoriesAndProductsTransformer';
import { useGovernmentAllocationsAllowedCategoriesAndProducts } from '@/features/privateCompanies/hooks/useGovernmentAllocationsAllowedCategoriesAndProducts';
import { useParams } from 'react-router-dom';
import { Option } from '@/common/types/option';
import ProductCategoryDistributionFormItemLoadingState from './ProductCategoryDistributionFormItemLoadingState';
import { UnitTextField } from "@/common/components/UnitTextField";
import { unities, unitiesDecimals } from "@/common/constants/unities.constant";

interface ProductCategoryDistributionFormItemProps {
  productCategoryIndex: number;
  productCategoryDistribution: CompanyGovernmentAllocationProductCategoriesDistribution;
  onDelete: () => void;
  onTotalValueChange: () => void;
  distribution: CompanyGovernmentAllocationDistribution;
  companyBranchId?: string;
  controlledBy?: ControlledByType;
}

const ProductCategoryDistributionFormItem = ({
  productCategoryIndex,
  productCategoryDistribution,
  onTotalValueChange,
  distribution,
  companyBranchId,
  controlledBy = 'currency',
}: ProductCategoryDistributionFormItemProps) => {
  const { id } = useParams()

  const uniqueId = useId();

  const isQuantity = controlledBy === 'quantity';

  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CreateGovernmentAllocationDistributionValidationSchema | CreateGovernmentAllocationDistributionQuantityValidationSchema>();

  const {
    data: allowedCategoriesAndProducts,
    isFetching: isAllowedCategoriesAndProductsFetching,
    isPending: isAllowedCategoriesAndProductsPending
  } = useGovernmentAllocationsAllowedCategoriesAndProducts<GovernmentAllocationAllowedCategoriesAndProducts>({
    governmentAllocationId: id,
    filters: { companyBranchId, productCategoryId: productCategoryDistribution?.product_category?.id },
  });

  const allowedCategory = useMemo(() => {
    return isAllowedCategoriesAndProductsPending
      ? undefined
      : allowedCategoriesAndProducts['categories'][productCategoryDistribution?.product_category?.id]
  }, [isAllowedCategoriesAndProductsPending]);

  const allowedProductsOptions = useMemo(() => {
    return isAllowedCategoriesAndProductsPending
      ? []
      : GovernmentAllocationsAllowedCategoriesAndProductsTransformer.optionsTransformer('products', controlledBy)(allowedCategoriesAndProducts || {})
  }, [isAllowedCategoriesAndProductsPending]);

  const {
    fields: productsDistributions,
    append: appendProductDistribution,
    remove: removeProductDistribution,
  } = useFieldArray({
    control,
    name: `product_categories_distributions.${productCategoryIndex}.products_distributions`,
  });

  const setProductCategoryTotalValue = () => {
    setTimeout(() => {
      if (isQuantity) {
        const productCategoriesTotalQuantitySum = productsDistributions.reduce((sum, _, index) => {
          const productTotalQuantity = watch(`product_categories_distributions.${productCategoryIndex}.products_distributions.${index}.total_quantity` as `product_categories_distributions.${number}.products_distributions.${number}.total_value`);

          return sum + (productTotalQuantity ? unFormatUnknownUnit(productTotalQuantity) : 0);
        }, 0);

        setValue(
          `product_categories_distributions.${productCategoryIndex}.total_quantity` as `product_categories_distributions.${number}.total_value`,
          formatDecimalUnit(productCategoriesTotalQuantitySum, productCategoryUnit)
        );
      } else {
        const productCategoriesTotalValueSum = productsDistributions.reduce((sum, _, index) => {
          const productTotalValue = watch(`product_categories_distributions.${productCategoryIndex}.products_distributions.${index}.total_value`);

          return sum + (productTotalValue ? unFormatCurrency(productTotalValue) : 0);
        }, 0);

        setValue(`product_categories_distributions.${productCategoryIndex}.total_value`, formatCurrency(productCategoriesTotalValueSum));
      }

      onTotalValueChange();
    });
  };

  const currentValue = useWatch({
    control,
    name: isQuantity
      ? `product_categories_distributions.${productCategoryIndex}.total_quantity`
      : `product_categories_distributions.${productCategoryIndex}.total_value`,
  });

  const allocated = Number(isQuantity ? allowedCategory?.allocated_quantity : allowedCategory?.allocated_total) || 0;
  const available = Number(isQuantity ? allowedCategory?.available_quantity : allowedCategory?.available_total) || 0;
  const distributed = Number(isQuantity ? allowedCategory?.distributed_quantity : allowedCategory?.distributed_total) || 0;

  const category = distribution?.product_categories_distributions
    ?.find((category) => category.product_category.id === productCategoryDistribution?.product_category?.id);

  const availableForAllocation =
    Math.min(
      isQuantity ? unFormatUnknownUnit(currentValue || '0') : unFormatCurrency(currentValue || ''),
      Number(isQuantity ? category?.total_quantity : category?.total_value) || 0,
    ) + available;

  const productCategoryUnit = useMemo(() => {
    const firstProductUnit = (productsDistributions || [])[0]?.product.unit_for_quantity;

    return (productsDistributions || []).every(p => p.product.unit_for_quantity === firstProductUnit)
      ? firstProductUnit
      : undefined;
  }, [(productsDistributions || []).length]);

  useEffect(() => {
    productsDistributions?.length > 0 && setProductCategoryTotalValue();
  }, [productsDistributions?.length]);

  if (isAllowedCategoriesAndProductsPending)
    return (
      <ProductCategoryDistributionFormItemLoadingState
        productsDistributionsLength={productsDistributions.length || 0}
      />
    )

  return (
    <Box className="flex flex-col gap-4">
      <Badge
        Icon={BoxesIcon}
        iconProps={{ className: 'size-6', strokeWidth: 1 }}
        text={allowedCategory?.display_name || ''}
        className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 pe-2 gap-0"
        overwriteClassName
        contentClassName="px-3 py-2"
      />

      <div className='flex flex-col gap-1'>
        {isQuantity ? (
          <UnitTextField
            label="Quantidade limite"
            decimals={productCategoryUnit ? unitiesDecimals[productCategoryUnit] : 2}
            unit={productCategoryUnit ? unities[productCategoryUnit] : undefined}
            autoCorrect="off"
            {...register(`product_categories_distributions.${productCategoryIndex}.total_quantity` as `product_categories_distributions.${number}.total_value`)}
            forceValue
            value={watch(`product_categories_distributions.${productCategoryIndex}.total_quantity` as `product_categories_distributions.${number}.total_value`)}
            onChange={(event) => {
              register(`product_categories_distributions.${productCategoryIndex}.total_quantity` as `product_categories_distributions.${number}.total_value`).onChange(event);
              onTotalValueChange();
            }}
            errorMessage={(errors.product_categories_distributions?.[productCategoryIndex] as { total_quantity?: { message?: string } })?.total_quantity?.message || undefined}
            disabled={productsDistributions?.length > 0}
          />
        ) : (
          <CurrencyTextField
            label="Valor limite"
            {...register(`product_categories_distributions.${productCategoryIndex}.total_value`)}
            onChange={(event) => {
              register(`product_categories_distributions.${productCategoryIndex}.total_value`).onChange(event);
              onTotalValueChange();
            }}
            errorMessage={(errors.product_categories_distributions?.[productCategoryIndex] as { total_value?: { message?: string } })?.total_value?.message || undefined}
            forceValue
            value={watch(`product_categories_distributions.${productCategoryIndex}.total_value`)}
            disabled={productsDistributions?.length > 0}
          />
        )}

        <div className='flex gap-3'>
          <p className='italic text-xs text-white-400'>
            {isQuantity ? 'Quantidade maxima: ' : 'Total máximo: '}
            <span className='whitespace-nowrap'>
              {isQuantity
                ? formatDecimalUnit(availableForAllocation, productCategoryUnit)
                : formatCurrency(availableForAllocation)}
            </span>
          </p>

          <p className='italic text-xs text-white-400'>
            {isQuantity ? 'Quantidade disponível: ' : 'Total disponível: '}
            <span className='whitespace-nowrap'>
              {isQuantity
                ? formatDecimalUnit(available, productCategoryUnit)
                : formatCurrency(available)}
            </span>
          </p>

          <p className='italic text-xs text-white-400'>
            {isQuantity ? 'Quantidade empenhada: ' : 'Total empenhado: '}
            <span className='whitespace-nowrap'>
              {isQuantity
                ? (allocated ? formatDecimalUnit(allocated, productCategoryUnit) : '--')
                : (allocated ? formatCurrency(allocated) : '--')}
            </span>
          </p>

          <p className='italic text-xs text-white-400'>
            {isQuantity ? 'Quantidade distribuída em todas filiais: ' : 'Total distribuído em todas filiais: '}
            <span className='whitespace-nowrap'>
              {isQuantity
                ? (distributed ? formatDecimalUnit(distributed, productCategoryUnit) : '--')
                : (distributed ? formatCurrency(distributed) : '--')}
            </span>
          </p>
        </div>
      </div>

      <Select
        placeholder="Selecione um produto"
        isLoading={isAllowedCategoriesAndProductsFetching}
        options={allowedProductsOptions?.filter((productOption) => {
          return !productsDistributions?.some(({ product }) => product?.id === productOption.value);
        }) || []}
        value=""
        onChange={(value) => {
          const newValue = { ...(value as GovernmentAllocationAllowedCategoryAndProductData & Option) }

          if (isQuantity) {
            appendProductDistribution({
              product: {
                id: newValue.value,
                display_name: newValue.display_name,
                unit_for_quantity: newValue.unit_for_quantity,
                category: {
                  id: '',
                  display_name: '',
                  products_count: ''
                }
              },
              total_quantity: newValue?.consumed_quantity
                ? formatDecimalUnit(newValue?.consumed_quantity || '', newValue.unit_for_quantity)
                : formatDecimalUnit(0, newValue.unit_for_quantity),
            });
          } else {
            appendProductDistribution({
              product: {
                id: newValue.value,
                display_name: newValue.display_name,
                unit_for_quantity: newValue.unit_for_quantity,
                category: {
                  id: '',
                  display_name: '',
                  products_count: ''
                }
              },
              total_value: newValue?.consumed_total
                ? formatCurrency(Number(newValue?.consumed_total || ''))
                : formatCurrency(0),
            });
          }

          setProductCategoryTotalValue();
        }}
      />

      {productsDistributions?.map((productDistribution, index) => (
        <ProductDistributionFormItem
          key={`${uniqueId}-${productDistribution?.product?.id}`}
          productCategoryIndex={productCategoryIndex}
          productIndex={index}
          productDistribution={productDistribution as CompanyGovernmentAllocationProductDistribution}
          allowedProduct={allowedCategoriesAndProducts['products'][productDistribution?.product?.id]}
          onDelete={() => {
            removeProductDistribution(index);
            setProductCategoryTotalValue();
          }}
          onTotalValueChange={setProductCategoryTotalValue}
          distribution={distribution}
          productCategoryId={productCategoryDistribution?.product_category?.id}
          controlledBy={controlledBy}
        />
      ))}
    </Box>
  );
};

export default ProductCategoryDistributionFormItem;
